import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Input } from '@/shared/ui';
import { track } from '@/lib/analytics';
import { useAuth } from '../hooks/use-auth';

interface ForgotPasswordFormProps {
  initialEmail?: string;
  onBackToSignIn: () => void;
}

/**
 * O pedido de redefinição de senha, como no `enamed-feedback-lp`. A
 * confirmação **não diz se a conta existe** ("se existir uma conta…"): dizer
 * entregaria a quem testa e-mails quais estão cadastrados.
 */
export function ForgotPasswordForm({
  initialEmail,
  onBackToSignIn,
}: ForgotPasswordFormProps) {
  const auth = useAuth();
  const request = useMutation({
    mutationFn: (email: string) => auth.forgotPassword(email),
    onSuccess: () => track({ event: 'senha_redefinicao_solicitada' }),
  });
  const [email, setEmail] = useState(() => initialEmail ?? '');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    request.mutate(email);
  };

  if (request.isSuccess) {
    return (
      <div className="form-stack">
        <div className="notice">
          Se existir uma conta com <strong>{email}</strong>, o link de
          redefinição chega nos próximos minutos. Confira também o spam.
        </div>
        <Button type="button" onClick={onBackToSignIn}>
          Voltar para o login
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="form-stack">
      <Input
        id="forgotEmail"
        label="E-mail"
        type="email"
        required
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="seu@email.com"
        helperText="Enviaremos um link para você criar uma senha nova."
      />

      {request.isError && (
        <div className="alert" role="alert">
          Não conseguimos enviar o link agora. Tente de novo em instantes.
        </div>
      )}

      <Button type="submit" disabled={request.isPending || !email.trim()}>
        {request.isPending ? 'Enviando…' : 'Enviar link de redefinição'}
      </Button>

      <p className="modal-switch">
        <button type="button" onClick={onBackToSignIn}>
          ← Voltar para o login
        </button>
      </p>
    </form>
  );
}
